import React from 'react';
import { v4 } from 'uuid';

import { useIpc } from '~src/main/utils/ipc-utils';
import { useFullscreenIframeController } from './fullscreen-iframe-controller';

interface FullscreenRenderProps {
  componentName: string;
  allProps: Record<string, any>;
}

/*
 * Extract component name, params and callback functions and pass it in a rpc
 * fashion to render in fullscreen iframe.
 */
export const FullscreenRender: React.FC<FullscreenRenderProps> = ({ componentName, allProps }) => {
  /*
   * Provide unique id to this component to filter ipc messages.
   */
  const [renderId] = React.useState<string>(() => v4());

  const ipc = useIpc();

  useFullscreenIframeController();

  /*
   * Functions will not be passed by copy to fullscreen iframe, but we will
   * create stubs in a rpc fashion.
   */
  const callbackPropsList: string[] = [];
  const staticProps = Object.assign({}, allProps);

  Object.keys(allProps).forEach(propName => {
    if (typeof staticProps[propName] === 'function') {
      delete staticProps[propName];
      callbackPropsList.push(propName);
    }
  });

  React.useEffect(() => {
    return ipc.listen(payload => {
      if (payload.name === 'run_fullscreen_callback') {
        /*
         * Callback function stub was invoked in fullscreen, run the real
         * function here.
         */
        if (payload.data.renderId !== renderId) {
          return;
        }
        const propName = payload.data.propName as string;
        const callback = allProps[propName];
        if (!callback) {
          throw `Callback with name "${propName}" not defined`;
        }
        callback(...payload.data.args);
      }
    });
  });

  React.useEffect(
    () => {
      ipc.send({
        name: 'render_fullscreen_component',
        data: {
          renderId,
          componentName,
          staticProps,
          callbackPropsList,
        },
      });
    } /* synchronize every change of props with fullscreen. */
  );

  React.useEffect(() => {
    return () => {
      ipc.send({
        name: 'hide_fullscreen_component',
        data: { renderId },
      });
    };
  }, []);

  return null;
};
